if (!customElements.get('rc-request-form')) {
  customElements.define('rc-request-form', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.form = this.querySelector('form.rc-request');
      if (!this.form) return;
      const { signal } = this.controller;
      const parameters = new URL(window.location.href).searchParams;
      const feedback = this.form.querySelector('[data-rc-form-feedback]');
      this.from = this.form.querySelector('[data-rc-date-from]');
      this.to = this.form.querySelector('[data-rc-date-to]');
      this.rentalTerm = this.form.querySelector('[data-rc-rental-term]');

      if (!feedback) this.prefill(parameters);
      if (this.rentalTerm) {
        this.rentalTerm.addEventListener('change', () => this.updateRentalTerm(), { signal });
        this.updateRentalTerm();
      }
      if (this.from && this.to) {
        for (const input of [this.from, this.to]) {
          input.addEventListener('input', () => this.validateDates(), { signal });
          input.addEventListener('change', () => this.validateDates(), { signal });
        }
        this.validateDates();
      }

      const servicePage = this.closest('.rc-service-page');
      if (servicePage) {
        servicePage.addEventListener('click', (event) => {
          const link = event.target.closest?.('[data-rc-service-select]');
          if (link) this.setIssue(link.dataset.rcServiceSelect);
        }, { signal });
      }

      const rentalPage = this.closest('.rc-rental-page');
      if (rentalPage) {
        rentalPage.addEventListener('click', (event) => {
          const link = event.target.closest?.('[data-rc-rental-select]');
          if (!link) return;
          const card = link.closest('[data-rc-rental-offer]');
          const selectedTerm = card?.querySelector('[data-rc-offer-term]:checked');
          const productField = this.form.querySelector('[data-rc-rental-product]');
          if (productField) {
            productField.value = link.dataset.rcRentalSelect;
            productField.dispatchEvent(new Event('input', { bubbles: true }));
          }
          if (this.rentalTerm && selectedTerm) {
            this.rentalTerm.value = selectedTerm.value;
            this.rentalTerm.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }, { signal });
      }

      this.form.addEventListener('submit', (event) => {
        if (event.defaultPrevented) return;
        this.validateDates();
        if (this.pending || !this.form.checkValidity()) {
          event.preventDefault();
          if (!this.pending) this.form.reportValidity();
          return;
        }
        queueMicrotask(() => {
          if (event.defaultPrevented || !this.isConnected) return;
          this.pending = true;
          this.form.setAttribute('aria-busy', 'true');
          this.submitButtons = [...this.form.querySelectorAll('button[type="submit"], input[type="submit"]')]
            .filter((button) => !button.disabled);
          for (const button of this.submitButtons) button.disabled = true;
          this.pendingTimer = window.setTimeout(() => this.resetPending(), 15000);
        });
      }, { signal });
      window.addEventListener('pageshow', () => this.resetPending(), { signal });

      if (feedback) {
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          feedback.focus({ preventScroll: true });
          feedback.scrollIntoView({ block: 'center', behavior: 'auto' });
        });
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resetPending();
    }

    resetPending() {
      window.clearTimeout(this.pendingTimer);
      this.pending = false;
      this.form?.removeAttribute('aria-busy');
      for (const button of this.submitButtons || []) button.disabled = false;
      this.submitButtons = [];
    }

    validateDates() {
      if (!this.from || !this.to) return;
      const date = new Date();
      const today = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      this.from.min = today;
      this.to.min = this.from.value && this.from.value > today ? this.from.value : today;
      this.from.setCustomValidity(this.from.value && this.from.value < today ? this.dataset.pastDateError || '' : '');
      this.to.setCustomValidity(this.to.value && this.from.value && this.to.value < this.from.value ? this.dataset.dateError || '' : '');
    }

    updateRentalTerm() {
      if (!this.rentalTerm || !this.to) return;
      const longTerm = ['36', '48', '60'].includes(this.rentalTerm.value);
      const endField = this.to.closest('[data-rc-rental-date-end]');
      this.to.required = !longTerm;
      this.to.disabled = longTerm;
      if (endField) endField.hidden = longTerm;
      if (longTerm) this.to.setCustomValidity('');
      else this.validateDates();
    }

    setIssue(key) {
      const field = this.form.querySelector('[data-rc-issue]');
      const option = [...(field?.options || [])].find((item) => item.dataset.rcIssueKey === key);
      if (option) field.value = option.value;
    }

    prefill(parameters) {
      const text = (key, length = 300) => (parameters.get(key) || '').replace(/[\u0000-\u001f\u007f]/g, '').slice(0, length);
      const set = (selector, value) => {
        const input = this.form.querySelector(selector);
        if (input && !input.value && value) input.value = value;
      };
      set('[data-rc-rental-product]', text('hyra'));
      set('[data-rc-machine]', text('maskin'));
      this.setIssue(text('arende', 40));

      const context = this.form.querySelector('[data-rc-product-context]');
      const product = text('produkt');
      if (!context || !product) return;
      context.hidden = false;
      set('[data-rc-context-product]', product);
      set('[data-rc-context-variant]', text('variantnamn'));
      set('[data-rc-context-sku]', text('sku', 100));
      const quantity = text('antal', 16);
      set('[data-rc-context-quantity]', /^\d+$/.test(quantity) && Number.isSafeInteger(Number(quantity)) && Number(quantity) > 0 ? quantity : '1');
      const variantId = text('variant', 30);
      if (/^\d+$/.test(variantId)) set('[data-rc-context-id]', variantId);
      this.setIssue(text('arende', 40) === 'leasing' ? 'leasing' : 'product');

      const productLink = text('produktlank', 2000);
      if (!productLink) return;
      try {
        const url = new URL(productLink, window.location.origin);
        if (url.origin !== window.location.origin || url.username || url.password || !/(^|\/)products\/[^/]+/.test(url.pathname)) return;
        const variant = url.searchParams.get('variant');
        url.search = '';
        url.hash = '';
        if (variant && /^\d{1,30}$/.test(variant)) url.searchParams.set('variant', variant);
        set('[data-rc-context-link]', url.href);
        const link = this.form.querySelector('[data-rc-context-open]');
        if (link) {
          link.href = url.href;
          link.hidden = false;
        }
      } catch {}
    }
  });
}
